import { useEffect, useState } from "react";
import { FORM_KIND_FR, type FormKind } from "../../shared/vault";
import { MAIN_DEFAULT_LOOK } from "../../shared/character";
import { Perso } from "../components/avatar";
import { Card, Loading, Notice } from "../components/ui";
import { VaultAddForm } from "../components/vault-form";
import { api, errorText } from "../lib/api";
import { usePageTitle } from "../lib/hooks";

/**
 * /coffre/ajouter?jeton=… — the one-tap link the agent sends (« Ajoute ta carte ici : … »), usable without a session
 * (an iPhone that never signed in): the add form for ONE item of the kind the link was made for. The token is
 * write-only (it reads nothing of the vault), single-use and valid 30 minutes; it never leaves this page but in the
 * JSON body of the two requests (never in a log line).
 */
export function VaultAddPage({ token: fromUrl }: { token: string | undefined }) {
  usePageTitle("Ajouter au coffre");
  // Read once: the address bar is cleaned right after, and the router then renders without it.
  const [token] = useState(fromUrl);
  const [info, setInfo] = useState<{ kind: FormKind; site: string | null; label: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<number | null>(null);

  useEffect(() => {
    // The token leaves the address bar at once (no history entry, no screenshot of it).
    if (typeof window !== "undefined" && window.location.search) window.history.replaceState(null, "", window.location.pathname);
    if (!token) {
      setError("Ce lien est incomplet : demande-en un nouveau à ton agent.");
      return;
    }
    let live = true;
    api("POST /api/coffre-lien/info", { body: { jeton: token }, redirectOn401: false })
      .then((r) => live && setInfo(r))
      .catch((err: unknown) => live && setError(errorText(err)));
    return () => {
      live = false;
    };
  }, [token]);

  return (
    <main className="min-h-dvh bg-bg px-4 pt-[6vh] pb-10">
      <div className="mx-auto w-full max-w-xl space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <Perso look={MAIN_DEFAULT_LOOK} size={96} mood={added !== null ? "joie" : "repos"} label="Ariston" />
          <h1 className="font-display text-[30px] leading-tight font-medium">Ajouter au Coffre</h1>
        </div>
        {error ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : added !== null ? (
          <Notice tone="success" role="status">
            C'est gardé dans ton coffre (n°{added}), chiffré. Tu peux fermer cette page : ton agent s'en servira sans jamais voir les valeurs.
          </Notice>
        ) : !info ? (
          <Loading />
        ) : (
          <Card className="space-y-4 p-5 sm:p-6">
            <p className="text-[14px] text-muted">
              {FORM_KIND_FR[info.kind]}
              {info.site ? ` pour ${info.site.replace(/^https?:\/\//, "")}` : ""} : saisis-le ici. Les valeurs partent une seule fois, chiffrées aussitôt ; ton agent ne les verra jamais, il s'en servira seulement pour toi.
            </p>
            <VaultAddForm
              kinds={[info.kind]}
              initialKind={info.kind}
              initialSite={info.site}
              siteLocked={!!info.site}
              initialLabel={info.label}
              submitLabel="Ajouter au coffre"
              onSubmit={async (input) => {
                try {
                  return await api("POST /api/coffre-lien", { body: { jeton: token!, item: input }, redirectOn401: false });
                } catch (err) {
                  throw new Error(errorText(err));
                }
              }}
              onDone={(n) => setAdded(n)}
            />
          </Card>
        )}
      </div>
    </main>
  );
}
